/**
 * KTAS 확대뷰 — 활력징후 베드사이드 모니터 UI (2열×3행, 지표별 고정 색상)
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TriagePatientRow } from './triageShared';

/** 참고 디자인: BP 빨강, HR 녹색, RR 크림, T 흰색, SpO₂ 하늘, BST 앰버 */
const MONITOR = {
    bp: '#EE3F33',
    hr: '#39B54A',
    rr: '#FFF1A1',
    t: '#FFFFFF',
    spo2: '#7AB6E6',
    bst: '#FBB03B',
} as const;

type VitalSource = Pick<
    TriagePatientRow,
    | 'vitalSigns'
    | 'respiration'
    | 'pulse'
    | 'bloodPressure'
    | 'temperature'
    | 'spo2'
    | 'bloodGlucose'
>;

function firstInt(s: string): string {
    const m = String(s).match(/(\d+(?:\.\d+)?)/);
    return m ? m[1] : '';
}

/**
 * vitalSigns·구조필드에서 BP/HR/RR/T/SpO₂/BST 추출
 */
function parseMonitorVitals(row: VitalSource): {
    bp: string;
    hr: string;
    rr: string;
    temp: string;
    spo2: string;
    bst: string;
} {
    const raw = (row.vitalSigns ?? '').trim();
    let s = raw;

    let bp = (row.bloodPressure ?? '').trim();
    if (!bp) {
        const bpM = s.match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
        if (bpM) {
            bp = `${bpM[1]}/${bpM[2]}`;
            s = s.replace(bpM[0], ' ');
        }
    }

    let hr = (row.pulse ?? '').trim();
    if (hr) hr = firstInt(hr);
    if (!hr) {
        const hrM = raw.match(/(?:HR|맥박|Heart\s*rate)[:\s]*(\d+)/i);
        if (hrM) hr = hrM[1];
    }

    let rr = (row.respiration ?? '').trim();
    if (rr) rr = firstInt(rr);
    if (!rr) {
        const rrM = raw.match(/(?:RR|호흡|Resp)[:\s]*(\d+)/i);
        if (rrM) rr = rrM[1];
    }

    let temp = (row.temperature ?? '').trim();
    if (!temp) {
        const tM =
            raw.match(/(?:체온|T|Temp)[:\s]*(\d{2}(?:\.\d)?)\s*°?\s*C?/i) ??
            raw.match(/\b(\d{2}(?:\.\d))\s*°\s*C\b/i);
        if (tM) temp = tM[1];
    }

    let spo2 = (row.spo2 ?? '').trim();
    if (spo2) spo2 = firstInt(spo2);
    if (!spo2) {
        const spM =
            raw.match(/(?:SpO₂|SpO2|산소)[:\s]*(\d{1,3})\s*%?/i) ?? raw.match(/\b(\d{2,3})\s*%\b/);
        if (spM) spo2 = spM[1];
    }

    let bst = (row.bloodGlucose ?? '').trim();
    if (bst) bst = firstInt(bst);
    if (!bst) {
        const bgM =
            raw.match(/(?:BST|혈당|Glucose|Blood\s*sugar)[:\s]*(\d{2,3})\s*mg/i) ??
            raw.match(/\b(\d{2,3})\s*mg\s*\/\s*dl\b/i);
        if (bgM) bst = bgM[1];
    }

    return {
        bp,
        hr,
        rr,
        temp,
        spo2,
        bst,
    };
}

function MonitorReadout({
    label,
    value,
    unit,
    color,
}: {
    label: string;
    value: string;
    unit: string;
    color: string;
}): React.ReactElement {
    const empty = value === '';
    const c = empty ? '#6b7280' : color;
    return (
        <div className="flex items-center min-w-0 gap-1">
            <span
                className="text-sm font-medium uppercase leading-snug tracking-wide sm:text-base"
                style={{ color: c }}
            >
                {label}
            </span>
            <span
                className="text-lg font-bold leading-none tabular-nums tracking-tight sm:text-lg"
                style={{ color: c }}
            >
                {empty ? '—' : value}
            </span>
            <span
                className="text-xs font-medium leading-snug sm:text-sm"
                style={{ color: c }}
            >
                {unit}
            </span>
        </div>
    );
}

export function KtasVitalMonitor({ row }: { row: VitalSource }): React.ReactElement {
    const { t } = useTranslation('class');
    const v = parseMonitorVitals(row);

    return (
        <div
            className="rounded-2xl bg-black p-5 shadow-inner ring-1 ring-white/10"
            role="region"
            aria-label={t('simulationResults.ktasVitalMonitorTitle')}
        >
            <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:gap-x-8 sm:gap-y-6">
                <MonitorReadout
                    label={t('simulationResults.ktasMonitorBp')}
                    value={v.bp}
                    unit={t('simulationResults.ktasMonitorUnitMmHg')}
                    color={MONITOR.bp}
                />
                <MonitorReadout
                    label={t('simulationResults.ktasMonitorHr')}
                    value={v.hr}
                    unit={t('simulationResults.ktasMonitorUnitPerMin')}
                    color={MONITOR.hr}
                />
                <MonitorReadout
                    label={t('simulationResults.ktasMonitorRr')}
                    value={v.rr}
                    unit={t('simulationResults.ktasMonitorUnitPerMin')}
                    color={MONITOR.rr}
                />
                <MonitorReadout
                    label={t('simulationResults.ktasMonitorT')}
                    value={v.temp}
                    unit={t('simulationResults.ktasMonitorUnitTemp')}
                    color={MONITOR.t}
                />
                <MonitorReadout
                    label={t('simulationResults.ktasMonitorSpo2')}
                    value={v.spo2}
                    unit={t('simulationResults.ktasMonitorUnitPct')}
                    color={MONITOR.spo2}
                />
                <MonitorReadout
                    label={t('simulationResults.ktasMonitorBst')}
                    value={v.bst}
                    unit={t('simulationResults.ktasMonitorUnitMgDl')}
                    color={MONITOR.bst}
                />
            </div>
        </div>
    );
}
